import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api';
import { addParamsToUrl } from 'utils';
import {
  PaginationResponse,
  CourseStepSubmission,
  CourseStepCurrentSubmission,
} from 'types';
import {
  cacheByIdArg,
  providesList,
  invalidatesList,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';
import {
  courseSubmissionsApi,
  courseSubmissionsTagType,
} from '../course-submissions';

import {
  ListRequest,
  ListResponse,
  CurrentStepRequest,
  SubmissionFormPayload,
  SubmissionResourcePayload,
  CourseStepCurrentSubmissionResponse,
} from './types';

const tagType = 'COURSE_STEP_SUBMISSIONS';

const tagTypes = [tagType];

export const courseStepSubmissionsApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'courseStepSubmissionsApi',
  endpoints: (build) => ({
    list: build.query<PaginationResponse<CourseStepSubmission>, ListRequest>({
      transformResponse: (response: ListResponse) => response.data,
      providesTags: (result) => providesList(tagType)(result?.data),
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.COURSE_STEP_SUBMISSIONS_LIST, params),
      }),
    }),

    current: build.query<CourseStepCurrentSubmission, CurrentStepRequest>({
      providesTags: (result, error, { id }) =>
        cacheByIdArg(tagType)(result, error, id),
      transformResponse: (response: CourseStepCurrentSubmissionResponse) =>
        response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.COURSE_STEP_SUBMISSIONS_CURRENT.replace(':id', id),
      }),
    }),

    submitForm: build.mutation<void, SubmissionFormPayload>({
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            courseSubmissionsApi.util.invalidateTags([courseSubmissionsTagType])
          );
        } catch (error) {
          console.error(error);
        }
      },
      query: (data) => {
        const { formId, isSubmitted, data: formSubmissionData } = data;
        return {
          method: 'patch',
          url: URL.COURSE_STEP_SUBMISSIONS_FORM.replace(':id', formId),
          data: {
            isSubmitted,
            formSubmissionData,
          },
        };
      },

      invalidatesTags: (result, error, arg) => {
        if (error) return [];

        // If isSubmitted is false, also invalidate current for this step
        if (arg.isSubmitted === false) {
          return cacheListAndByIdArgProperty(tagType)(result, error, {
            id: arg.stepId,
          });
        } else {
          return invalidatesList(tagType)();
        }
      },
    }),

    submitResource: build.mutation<void, SubmissionResourcePayload>({
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            courseSubmissionsApi.util.invalidateTags([courseSubmissionsTagType])
          );
        } catch (error) {
          console.error(error);
        }
      },
      query: (data) => {
        // courseStepId is needed for invalidatesTags
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { resourceId, courseStepId, ...payload } = data;
        return {
          data: payload,
          method: 'patch',
          url: URL.COURSE_STEP_SUBMISSIONS_RESOURCE.replace(':id', resourceId),
        };
      },
      invalidatesTags: (result, error, arg) => {
        if (error) return [];

        // If isSubmitted is false, also invalidate current for this step
        if (arg.isSubmitted === false) {
          return cacheListAndByIdArgProperty(tagType)(result, error, {
            id: arg.courseStepId,
          });
        } else {
          return invalidatesList(tagType)();
        }
      },
    }),
  }),
});

export const {
  useListQuery,
  useCurrentQuery,
  useSubmitFormMutation,
  useSubmitResourceMutation,
} = courseStepSubmissionsApi;

export { type ListRequest, tagType as courseStepSubmissionsTagType };
